import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { User, Lock, SignIn } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { formatApiErrorDetail } from "../lib/authApi";
import { firstAccessibleAdminPath, tournamentDetailId } from "../lib/adminMenuCatalog";
import Logo from "../components/common/Logo";
import BackButton from "../components/common/BackButton";
import GoogleSignInButton from "../components/auth/GoogleSignInButton";
import AppleSignInButton from "../components/auth/AppleSignInButton";
import InAppBrowserInlineWarning from "../components/inapp/InAppBrowserInlineWarning";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Phase 84.M.49 — `?invite=<id>` đến từ trang InviteLandingPage khi
  // người được mời đã có tài khoản nhưng chọn "Đăng nhập tài khoản khác".
  const inviteId = searchParams.get("invite");
  // Phase 85.A.49 — User cũ click bookmark `/auth/callback` (Emergent flow
  // đã deprecated) sẽ được redirect về `/login?legacy_oauth=1`. Show toast
  // thông báo dùng nút Google mới.
  const isLegacyOAuthRedirect = searchParams.get("legacy_oauth") === "1";
  const returnTo = location.state?.returnTo;

  useEffect(() => {
    if (isLegacyOAuthRedirect) {
      toast.info(
        "Phương thức đăng nhập Google đã được nâng cấp. Vui lòng bấm nút \"Đăng nhập với Google\" bên dưới.",
        { duration: 6000 },
      );
    }
  }, [isLegacyOAuthRedirect]);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const roleLanding = (me) => {
    // Phase 81i — Login redirect logic.
    // Phase 81cc — HARDENED redirect: dù bất kỳ returnTo nào, role-based
    // landing được ƯU TIÊN TUYỆT ĐỐI nhằm ngăn TT/BTC vô tình bị chuyển về
    // /athlete/profile (hoặc /referee/profile). Profile trọng tài CHỈ mở khi
    // user chủ động bấm nút "Hồ sơ" trong RefereeDashboard.
    //
    // Hierarchy:
    //   super_admin / organizer (BTC) / staff / admin → menu admin đầu tiên
    //   referee (kể cả khi kèm athlete)               → /referee
    //   athlete-only / publisher-only                  → /athlete/profile
    //   (Phase 85.A.181 — Publisher = CTV upload video, treat như athlete
    //    để landing về trang chủ public/VĐV thay vì /admin/dashboard.)
    const r = (me && me.roles) || [];
    const hasAdmin = r.includes("super_admin") || r.includes("organizer") || r.includes("admin") || r.includes("staff");
    const hasReferee = r.includes("referee");
    const hasAthlete = r.includes("athlete") || r.includes("publisher");

    // Phase 81cc — Compute role-based landing FIRST.
    // Phase 81vv — Athlete giờ có homepage VĐV mới mount tại "/" qua RootRedirect.
    // Phase 85.A.273 — admin-tier landing = menu ĐẦU TIÊN có quyền (không cứng
    // /admin/dashboard → tránh 403 cho organizer/admin không có menu dashboard).
    // v16.17 Phase C.41 (Feb 2026) — Publisher THUẦN (roles === ["publisher"])
    // + setting `pl.publisher.auto_redirect_disabled` KHÔNG = "1" (default ON)
    // → auto redirect về /publisher/auto?resume=1.
    // v16.60.2 (Feb 2026) — Trong APP NATIVE (WebView bridge đã set
    // `window.__pickleagueNativeApp`): mọi user có role "publisher" đều
    // được auto-redirect (kể cả khi kèm role athlete/khác).
    // v16.60.4 (Feb 2026) — Tôn trọng snooze 5 phút (nút Home ở
    // /publisher/auto). Áp dụng cả web + native.
    const isNativeApp = typeof window !== "undefined" && window.__pickleagueNativeApp === true;
    const hasPublisher = r.includes("publisher");
    const isPurePublisher = r.length === 1 && r[0] === "publisher";
    let inSnooze = false;
    try {
      const snoozeUntil = parseInt(window.localStorage.getItem("pl.pub.auto.snoozed_until") || "0", 10);
      inSnooze = !!(snoozeUntil && Date.now() < snoozeUntil);
    } catch { /* noop */ }
    let publisherAutoRedirect = false;
    if (isNativeApp && hasPublisher && !inSnooze) {
      publisherAutoRedirect = true;
    } else if (isPurePublisher) {
      try {
        const disabled = window.localStorage.getItem("pl.publisher.auto_redirect_disabled") === "1";
        publisherAutoRedirect = !disabled && !inSnooze;
      } catch { publisherAutoRedirect = !inSnooze; }
    }
    let landing;
    if (hasAdmin) landing = firstAccessibleAdminPath(me) || (hasReferee ? "/referee" : "/");
    else if (hasReferee) landing = "/referee";
    else if (publisherAutoRedirect) landing = "/publisher/auto?resume=1";
    else if (hasAthlete) landing = "/";
    else landing = "/";  // Phase 85.A.181 — default landing = trang chủ VĐV cho mọi role lạ.

    // Honor returnTo CHỈ KHI:
    //   1. returnTo nằm trong namespace của role landing (admin → /admin/*,
    //      referee → /referee/* TRỪ /referee/profile, athlete → /athlete/*)
    //   2. KHÔNG phải trang profile (tránh user bị "đẩy về profile" do bookmark
    //      cũ hoặc returnTo từ ProtectedRoute redirect).
    const isProfilePath = (p) =>
      p === "/referee/profile" || p === "/athlete/profile";

    if (returnTo && !isProfilePath(returnTo)) {
      // Phase 85.A.275 (2a) — user được gán quyền theo GIẢI cụ thể mở được
      // deep-link chi tiết giải đó dù role/menu bị giới hạn (vd VĐV được BTC
      // cấp quyền theo giải). Backend tự enforce per-tid.
      const assignedTids = (me && me.assigned_tournament_ids) || [];
      const rtTid = tournamentDetailId(returnTo);
      if (rtTid && assignedTids.includes(rtTid)) return returnTo;
      if (hasAdmin && returnTo.startsWith("/admin")) return returnTo;
      if (hasReferee && returnTo.startsWith("/referee")) return returnTo;
      if (hasAthlete && returnTo.startsWith("/athlete")) return returnTo;
    }
    return landing;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const me = await login(username.trim(), password);
      toast.success(`Xin chào, ${me.full_name || me.username}`);
      // Phase 84.M.49 — Nếu có inviteId, ưu tiên redirect tới landing.
      if (inviteId) {
        navigate(`/invite/${inviteId}`, { replace: true });
      } else {
        navigate(roleLanding(me), { replace: true });
      }
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Không đăng nhập được";
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060B14] text-white flex items-center justify-center px-5 py-10 relative overflow-hidden" data-testid="login-page-pickleague">
      <BackButton testId="login-back" />
      {/* Phase 81ww — PickLeague theme: dark + cam + lime glow ambient */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full opacity-40" style={{ background: "radial-gradient(circle, rgba(255,138,0,0.22) 0%, transparent 70%)" }} />
        <div className="absolute -bottom-40 -right-40 w-[560px] h-[560px] rounded-full opacity-35" style={{ background: "radial-gradient(circle, rgba(168,255,0,0.18) 0%, transparent 70%)" }} />
        {/* Floating particles (CSS keyframe homeFloat reused) */}
        {Array.from({ length: 8 }).map((_, i) => (
          <span
            key={i}
            className="absolute rounded-full"
            style={{
              left: `${(i * 17 + 9) % 92}%`,
              top: `${(i * 19 + 13) % 88}%`,
              width: i % 2 === 0 ? "4px" : "3px",
              height: i % 2 === 0 ? "4px" : "3px",
              background: i % 2 === 0 ? "rgba(168,255,0,0.6)" : "rgba(255,138,0,0.55)",
              boxShadow: i % 2 === 0 ? "0 0 12px rgba(168,255,0,0.7)" : "0 0 10px rgba(255,138,0,0.6)",
              animation: `homeFloat ${4 + (i % 4)}s ease-in-out ${i * 0.3}s infinite`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-3" style={{ filter: "drop-shadow(0 0 28px rgba(255,138,0,0.45))" }}>
            <Logo variant="full" size={120} priority alt="PickLeague" testid="login-logo" />
          </div>
          <p className="text-[#94A3B8] text-sm font-medium">Pickleball • Giải đấu • Cộng đồng</p>
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-[24px] border border-white/10 backdrop-blur-xl p-7 sm:p-8 shadow-[0_10px_30px_rgba(0,0,0,0.4)]"
          style={{ background: "linear-gradient(180deg, rgba(17,24,39,0.85) 0%, rgba(11,18,32,0.85) 100%)" }}
          data-testid="login-form"
        >
          <h1 className="font-heading text-[26px] sm:text-[28px] font-black tracking-tight mb-1">Đăng nhập</h1>
          <p className="text-[#94A3B8] text-sm mb-6">Welcome back champion 👑</p>

          {err && (
            <div
              className="mb-4 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/35 text-red-300 text-sm"
              data-testid="login-error"
            >
              {err}
            </div>
          )}

          <label className="block text-[11px] tracking-[0.2em] uppercase font-bold text-[#94A3B8] mb-2">
            Tên đăng nhập / SĐT
          </label>
          <div className="relative mb-4">
            <User size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#FF8A00]" weight="bold" />
            <input
              type="text"
              autoFocus
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              placeholder="Tên đăng nhập / SĐT"
              className="w-full bg-[#060B14]/90 border border-white/12 focus:border-[#FF8A00] focus:shadow-[0_0_16px_rgba(255,138,0,0.25)] outline-none pl-11 pr-4 py-3 rounded-[18px] text-sm transition"
              data-testid="login-username"
            />
          </div>

          <label className="block text-[11px] tracking-[0.2em] uppercase font-bold text-[#94A3B8] mb-2">
            Mật khẩu
          </label>
          <div className="relative mb-6">
            <Lock size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A8FF00]" weight="bold" />
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              className="w-full bg-[#060B14]/90 border border-white/12 focus:border-[#A8FF00] focus:shadow-[0_0_16px_rgba(168,255,0,0.22)] outline-none pl-11 pr-4 py-3 rounded-[18px] text-sm transition"
              data-testid="login-password"
            />
          </div>

          <button
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-white disabled:opacity-60 disabled:cursor-not-allowed font-black text-[14px] tracking-wide rounded-[18px] transition active:scale-[0.98] shadow-[0_0_28px_rgba(255,138,0,0.45)]"
            style={{ background: "linear-gradient(135deg, #FF8A00 0%, #FF9F1C 100%)" }}
            data-testid="login-submit"
          >
            <SignIn size={18} weight="bold" />
            {busy ? "Đang đăng nhập..." : "Đăng nhập"}
          </button>

          {/* Phase 83y.36 — divider + Google sign-in. */}
          <div className="my-4 flex items-center gap-3">
            <div className="flex-1 h-px bg-white/8" />
            <span className="text-[10px] tracking-[0.25em] uppercase text-white/40 font-bold">Hoặc</span>
            <div className="flex-1 h-px bg-white/8" />
          </div>
          {/* Phase 84.M.86 — inline warning khi user đang dùng in-app browser. */}
          <InAppBrowserInlineWarning flow="login-google" className="mb-3" />
          <GoogleSignInButton label="Đăng nhập với Google" />
          <div className="mt-3">
            <AppleSignInButton label="Đăng nhập với Apple" />
          </div>

          <div className="mt-5 flex items-center justify-between text-xs">
            <Link to="/forgot-password" className="text-[#94A3B8] hover:text-white transition" data-testid="login-forgot-link">
              Quên mật khẩu?
            </Link>
          </div>
        </form>

        {/* Phase 85.A.156 — Block highlight CTA "Đăng ký" cuối form login.
            Nhiều user mới truy cập "Đăng nhập" mà không biết phải đăng ký
            trước → đặt 1 panel rõ ràng + animation pulse glow để dễ thấy. */}
        <Link
          to="/register"
          data-testid="login-register-cta-highlight"
          className="group mt-4 block rounded-2xl border-2 border-[#A8FF00]/40 bg-gradient-to-br from-[#A8FF00]/[0.08] via-[#A8FF00]/[0.04] to-transparent hover:border-[#A8FF00]/70 hover:from-[#A8FF00]/[0.15] active:scale-[0.98] transition relative overflow-hidden"
        >
          {/* Animated glow viền */}
          <span aria-hidden className="absolute inset-0 rounded-2xl pointer-events-none animate-pulse" style={{ boxShadow: "0 0 24px 2px rgba(168,255,0,0.25)" }} />
          <div className="relative px-4 py-3.5 flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#A8FF00]/85 font-black">
                Chưa có tài khoản?
              </div>
              <div className="mt-0.5 text-[15px] font-black text-white leading-tight">
                Đăng ký miễn phí ngay →
              </div>
              <div className="mt-0.5 text-[10.5px] text-white/55 leading-snug">
                Tham gia giải đấu, theo dõi điểm trình, kết nối cộng đồng Pickleball.
              </div>
            </div>
            <div className="shrink-0 w-10 h-10 rounded-xl bg-[#A8FF00]/15 border border-[#A8FF00]/40 flex items-center justify-center group-hover:bg-[#A8FF00]/30 transition">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#A8FF00" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </Link>

        <p className="text-center mt-6 text-xs text-[#94A3B8]/60">
          © PickLeague 2026 · Premium Pickleball Tournament Platform
        </p>
      </div>
    </div>
  );
}
